import React, { useCallback, useEffect, useMemo, useState } from "react";
import MetricTable from "../Metric-Table/MetricTable";
import ErrorDialog from "../ErrorDialog/ErrorDialog";
import LoadingStyle from "../../Loading/Loading";
import apiClientTimeseries from "../../../services/apiClientTimeseries";
import { getHeaders as getMetricHeaders } from "../../ApiHeaders/apiHeadersTimeseries";
import apiClientAdmin from "../../../services/apiClientAdmin";
import { getHeaders as getAssetHeaders } from "../../ApiHeaders/apiHeaders";
import { SplitDivider, useResizableSplit } from "../../SplitPane/ResizableSplit";
import { useEntitlements } from "../../DataContext/EntitlementsContext";

interface VendorMapping {
  vendor_source_id?: number;
  vendor_source_name: string;
  vendor_table: string;
  data_collection_frequency: string;
}

interface VendorSource {
  vendor_source_id: number;
  vendor_source_name: string;
}

const METRIC_SECTIONS = [
  {
    title: "Classification",
    fields: [
      ["ISO Code", "iso_code"],
      ["Fuel Type", "fuel_type"],
      ["Category", "category"],
      ["Market Type", "market_type"],
      ["Product", "product"],
      ["Status", "status"],
    ],
  },
  {
    title: "Configuration",
    fields: [
      ["Units", "units"],
      ["Base Granularity", "base_granularity"],
      ["Base Entity", "base_entity_name"],
      ["Award Type", "award_type"],
      ["Aggregation Function", "agg_func_id"],
      ["Time to Live", "time_to_live"],
    ],
  },
  {
    title: "Settlement",
    fields: [
      ["Settlement Charge Code", "settle_charge_code"],
      ["Settlement Charge Name", "settle_charge_name"],
    ],
  },
] as const;

const makeMetricKey = (metric: any) =>
  `${metric.metric_name}|${metric.iso_code}|${metric.category}|${metric.market_type}|${metric.fuel_type}|${metric.product}`;

interface MetricScreenProps {
  /** Admin on this page: may create, edit and delete. Otherwise the page is view-only. */
  canDelete?: boolean;
}

// Read-only already: no control here changes data, so canDelete gates nothing yet.
const MetricScreen: React.FC<MetricScreenProps> = () => {
  const { entitlements } = useEntitlements();
  // PI mappings list every asset's resources and the API is granted to admins only,
  // so everyone else (e.g. Knowledge Center readers) skips the call and the section.
  const canViewPiMappings = entitlements.some(
    (e) =>
      e.po_path?.toLowerCase().trim() === "/administration" &&
      e.privilege?.toLowerCase().trim() === "admin"
  );
  const [metrics, setMetrics] = useState<any[]>([]);
  const [metricGroups, setMetricGroups] = useState<Record<string, any[]>>({});
  const [piRecords, setPiRecords] = useState<any[]>([]);
  const [vendorMappings, setVendorMappings] = useState<VendorMapping[]>([]);
  const [vendorSources, setVendorSources] = useState<VendorSource[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<any | null>(null);
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ title: string; details: string } | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await getMetricHeaders();
      const response = await apiClientTimeseries.get("/metric-config/", { headers });
      const allMetrics = response?.data?.response?.data || [];
      const groups = allMetrics.reduce((result: Record<string, any[]>, metric: any) => {
        const key = makeMetricKey(metric);
        (result[key] ||= []).push(metric);
        return result;
      }, {});

      setMetricGroups(groups);
      const tableRows = (Object.values(groups) as any[][]).map((group) => group[0]);
      setMetrics(tableRows);
    } catch (requestError: any) {
      setError({
        title: "Failed to Fetch Metrics",
        details:
          requestError?.response?.data?.error?.message ||
          requestError?.message ||
          "Unable to load metrics.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    if (!canViewPiMappings) return;
    const fetchPIRecords = async () => {
      try {
        const headers = await getMetricHeaders();
        const response = await apiClientTimeseries.get(
          "/metric-resource/pi-metric-resource",
          { headers }
        );
        const records = response?.data?.response?.data || [];
        setPiRecords(records.filter((record: any) => record.PI_METRIC_ID != null));
      } catch (requestError) {
        console.error("Failed to fetch PI metric mappings", requestError);
      }
    };

    fetchPIRecords();
  }, [canViewPiMappings]);

  useEffect(() => {
    const fetchVendorSources = async () => {
      try {
        const headers = await getAssetHeaders();
        const response = await apiClientAdmin.get("/ref-data/vendor-sources/", { headers });
        setVendorSources(response?.data?.response?.data || []);
      } catch (requestError) {
        console.error("Failed to fetch vendor source options", requestError);
      }
    };

    fetchVendorSources();
  }, []);

  const selectedMetrics = useMemo(() => {
    if (!selectedMetric) return [];
    return metricGroups[makeMetricKey(selectedMetric)] || [selectedMetric];
  }, [metricGroups, selectedMetric]);

  const primaryMetric = selectedMetrics[0];
  const sources = selectedMetrics
    .map((metric) => metric.source)
    .filter(Boolean)
    .map((source) => (source === "nautilus" ? "Snowflake" : String(source).toUpperCase()));
  const piMappings = useMemo(() => {
    if (!selectedMetrics.length) return [];
    const metricNames = new Set(selectedMetrics.map((metric) => metric.metric_name));
    const metricKeys = new Set(selectedMetrics.map((metric) => metric.metric_internal_key));

    return piRecords.filter(
      (record) =>
        metricNames.has(record.METRIC_NAME) ||
        metricKeys.has(record.METRIC_INTERNAL_KEY)
    );
  }, [piRecords, selectedMetrics]);

  useEffect(() => {
    if (!primaryMetric?.metric_id) {
      setVendorMappings([]);
      return;
    }

    const fetchVendorMappings = async () => {
      try {
        const headers = await getAssetHeaders();
        const response = await apiClientAdmin.get(
          `/ref-data/metric-config/${primaryMetric.metric_id}/vendor-sources`,
          { headers }
        );
        setVendorMappings(response?.data?.response?.data || []);
      } catch (requestError: any) {
        setError({
          title: "Failed to Fetch Vendor Mappings",
          details:
            requestError?.response?.data?.error?.message ||
            requestError?.message ||
            "Unable to load vendor mappings.",
        });
      }
    };

    fetchVendorMappings();
  }, [primaryMetric?.metric_id]);

  const handleRowSelect = (metric: any) => {
    setSelectedMetric(metric);
    setSelectedRow(makeMetricKey(metric));
  };

  const split = useResizableSplit("xl");
  return (
    <div ref={split.containerRef} style={split.containerStyle} className={`flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}>
      <div className={`admin-tab-panel flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative ${split.leftClassName}`}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
            <LoadingStyle />
          </div>
        )}
        <MetricTable
          metrics={metrics}
          selectedMetric={selectedMetric}
          setSelectedMetric={setSelectedMetric}
          onRowSelect={handleRowSelect}
          selectedRow={selectedRow}
        />
      </div>

      <SplitDivider {...split.dividerProps} />
      <div className="admin-tab-panel admin-tab-form-panel flex-grow flex flex-col lg:basis-1/4 bg-white rounded-xl shadow-md p-5">
        {primaryMetric ? (
          <div className="space-y-5">
            <div className="border-b border-gray-200 pb-4">
              <p className="text-sm font-semibold text-gray-500">Metric Details</p>
              <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800 break-words">
                    {primaryMetric.metric_name || "-"}
                  </h2>
                  <p className="mt-1 text-base text-gray-600 break-words">
                    {primaryMetric.description || "No description available"}
                  </p>
                </div>
                <span className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700">
                  {sources.join(", ") || "No source"}
                </span>
              </div>
            </div>

            {METRIC_SECTIONS.map((section) => (
              <section key={section.title}>
                <h3 className="text-base font-semibold text-gray-800 mb-3">{section.title}</h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
                  {section.fields.map(([label, field]) => (
                    <div key={field} className="border-l-2 border-gray-100 pl-3">
                      <dt className="text-sm text-gray-500">{label}</dt>
                      <dd className="mt-1 text-base font-medium text-gray-800 break-words">
                        {primaryMetric[field] ?? "-"}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            ))}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 border-t border-gray-200 pt-5">
              <section>
                <h3 className="text-base font-semibold text-gray-800 mb-3">Source Records</h3>
                <div className="grid grid-cols-1 gap-3">
                {selectedMetrics.map((metric) => {
                  const sourceName = metric.source === "nautilus"
                    ? "Snowflake"
                    : metric.source
                    ? String(metric.source).toUpperCase()
                    : "Unknown Source";

                  return (
                    <div key={metric.metric_id ?? metric.source} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-base font-semibold text-gray-800">{sourceName}</p>
                        <span className="text-sm text-gray-500">ID {metric.metric_id ?? "-"}</span>
                      </div>
                      <div className="mt-3 space-y-2 text-sm">
                        <div>
                          <p className="text-sm text-gray-500">Metric Internal Key</p>
                          <p className="mt-1 text-base text-gray-800 break-words">{metric.metric_internal_key || "-"}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Base Entity</p>
                          <p className="mt-1 text-base text-gray-800 break-words">{metric.base_entity_name || "-"}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
              </section>

              <section>
                <h3 className="text-base font-semibold text-gray-800 mb-3">Vendor Sources</h3>
                {vendorMappings.length > 0 ? (
                  <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50">
                    <table className="w-full border-collapse text-left text-sm">
                      <thead className="sticky top-0 bg-gray-100 text-gray-600">
                        <tr>
                          <th className="px-3 py-2 font-medium">Vendor</th>
                          <th className="px-3 py-2 font-medium">Table</th>
                          <th className="px-3 py-2 font-medium">Frequency</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white text-gray-800">
                        {vendorMappings.map((mapping) => {
                          const vendorSource = vendorSources.find(
                            (source) => source.vendor_source_id === mapping.vendor_source_id
                          );
                          return (
                            <tr key={mapping.vendor_source_id ?? mapping.vendor_source_name}>
                              <td className="px-3 py-3 font-medium break-words">
                                {(mapping.vendor_source_name || vendorSource?.vendor_source_name) === "Snowflake"
                                  ? "YES Energy"
                                  : mapping.vendor_source_name || vendorSource?.vendor_source_name || "-"}
                              </td>
                              <td className="px-3 py-3 break-words">
                                {mapping.vendor_table || "-"}
                              </td>
                              <td className="px-3 py-3 break-words">
                                {mapping.data_collection_frequency || "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="border border-dashed border-gray-200 rounded-lg p-4 text-sm text-gray-500">
                    No vendor mappings are configured for this metric.
                  </p>
                )}
              </section>
            </div>

            {canViewPiMappings && (
              <section className="border-t border-gray-200 pt-5">
                <h3 className="text-base font-semibold text-gray-800 mb-3">PI Mappings</h3>
                {piMappings.length > 0 ? (
                  <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-200">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-white">
                      <p className="text-sm text-gray-500">Asset</p>
                      <p className="text-sm text-gray-500">Resource</p>
                      <p className="text-sm text-gray-500">PI Element</p>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {piMappings.map((record) => (
                        <div key={record.PI_METRIC_ID} className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-white">
                          <p className="text-base font-medium text-gray-800 break-words">
                            {record.BETM_OBJECT_NAME || "-"}
                          </p>
                          <p className="text-base font-medium text-gray-800 break-words">
                            {record.ISO_RESOURCE_NAME || "-"}
                          </p>
                          <p className="text-base font-medium text-gray-800 break-words">
                            {record.DATA_POINT_NAME || "-"}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="rounded-lg border border-dashed border-gray-200 px-4 py-3 text-sm text-gray-500">
                    No PI mappings are configured for this metric.
                  </p>
                )}
              </section>
            )}
          </div>
        ) : (
          <div className="h-full min-h-[400px] flex items-center justify-center text-center text-gray-500">
            <div>
              <h2 className="text-xl font-semibold mb-2">Select a metric</h2>
              <p className="text-sm text-gray-400">Choose a metric from the list to view its details.</p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <ErrorDialog
          title={error.title}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
};

export default MetricScreen;
